import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Container, Form, Button } from "react-bootstrap";
import { Eye, EyeOff, User as UserIcon, Lock, Mail, Phone } from "lucide-react";
import "../../../assets/css/home/Ngologin.css";
import axios from "axios";
import AppContext from "../../../context/AppContext";
import Swal from "sweetalert2";

const Ngologin = () => {
  const navigate = useNavigate();
  const { baseUrl, regExpchecker, storeLoggedInNgoId } = useContext(AppContext);

  const [isLoginMode, setIsLoginMode] = useState(true);
  const [password, setPassword]       = useState("");
  const [email, setEmail]             = useState("");
  const [fullname, setFullname]       = useState("");
  const [phone, setPhone]             = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors]           = useState({});

  // ✅ Validate before submit
  const validateBeforeSubmit = () => {
    const newErrors = {};

    if (!isLoginMode) {
      // Signup: sirf empty check
      if (!fullname || fullname.trim() === "") newErrors.name = "Full name is required";
      if (!phone || phone.trim() === "")       newErrors.phone = "Phone number is required";
      const passErr = regExpchecker("password", password);
      if (passErr) newErrors.password = passErr;
      const emailErr = regExpchecker("email", email);
      if (emailErr) newErrors.email = emailErr;
    } else {
      // Login: sirf empty check
      if (!email || email.trim() === "")       newErrors.email = "Email is required";
      if (!password || password.trim() === "") newErrors.password = "Password is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const toggleAuthMode = () => {
    setIsLoginMode(!isLoginMode);
    setErrors({});
    setEmail(""); setPassword(""); setFullname(""); setPhone("");
  };

  const handleSocialLogin = (provider) => {
    alert(`${provider} ${isLoginMode ? "Login" : "Signup"} will be integrated!`);
  };

  // ✅ Signup
  const ngoSignup = () => {
    if (!validateBeforeSubmit()) return;

    setIsSubmitting(true);
    const formData = new FormData();
    formData.append("fullname", fullname);
    formData.append("email",    email);
    formData.append("phone",    phone);
    formData.append("password", password);

    axios.post(`${baseUrl}/ngo/auth/signup`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((response) => {
        if (response.data.success) {
          Swal.fire({ title: "Congratulations!", text: "Account created successfully. Please Login", icon: "success" });
          toggleAuthMode();
        } else {
          const errMsg = response.data.message || response.data.error || "Cannot create account. Try again later.";
          Swal.fire({ title: "Oops!", text: errMsg, icon: "error" });
        }
      })
      .catch((error) => {
        const errMsg = error.response?.data?.message || "Something went wrong. Please try again.";
        Swal.fire({ title: "Error!", text: errMsg, icon: "error" });
      })
      .finally(() => setIsSubmitting(false));
  };

  // ✅ Login
  const ngoLogin = () => {
    if (!validateBeforeSubmit()) return;

    setIsSubmitting(true);
    const formData = new FormData();
    formData.append("email",    email);
    formData.append("password", password);

    axios.post(`${baseUrl}/ngo/auth/login`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((response) => {
        if (response.data.token) {
          // ✅ localStorage mein store karo
          localStorage.setItem("loggedInNgo", JSON.stringify(response.data.ngo));
          localStorage.setItem("ngoemail",    email);
          localStorage.setItem("ngopassword", password);
          // ✅ ID AppContext mein store karo
          storeLoggedInNgoId(response.data.ngo.id);
          navigate("/ngo/dashboard");
        } else {
          const errMsg = response.data.Message || response.data.message || response.data.error || "Invalid email or password.";
          Swal.fire({ title: "Login Failed!", text: errMsg, icon: "error" });
        }
      })
      .catch((error) => {
        const errMsg = error.response?.data?.message || "Something went wrong. Please try again.";
        Swal.fire({ title: "Error!", text: errMsg, icon: "error" });
      })
      .finally(() => setIsSubmitting(false));
  };

  // ✅ Auto Login
  const ngoAutoLogin = (em, pw) => {
    const formData = new FormData();
    formData.append("email",    em);
    formData.append("password", pw);

    axios.post(`${baseUrl}/ngo/auth/login`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((response) => {
        if (response.data.token) {
          localStorage.setItem("loggedInNgo", JSON.stringify(response.data.ngo));
          storeLoggedInNgoId(response.data.ngo.id);
          navigate("/ngo/dashboard");
        }
      })
      .catch((error) => console.log(error));
  };

  useEffect(() => {
    const storedEmail    = localStorage.getItem("ngoemail");
    const storedPassword = localStorage.getItem("ngopassword");
    if (storedEmail && storedPassword) {
      ngoAutoLogin(storedEmail, storedPassword);
    }
  }, []);

  return (
    <div className="login-page">
      <div className="login-wrapper">
        <div className="login-container">

          {/* Left Side - Form */}
          <div className="login-form-section">
            <h2 className="login-title">{isLoginMode ? "Login" : "Sign Up"}</h2>

            <Form onSubmit={(e) => {
              e.preventDefault();
              if (isLoginMode) { ngoLogin(); } else { ngoSignup(); }
            }}>

              {/* Full Name - signup only */}
              {!isLoginMode && (
                <Form.Group className="form-group">
                  <div className="input-wrapper">
                    <UserIcon className="input-icon" size={20} />
                    <Form.Control
                      type="text"
                      placeholder="Full Name"
                      value={fullname}
                      onChange={(e) => setFullname(e.target.value)}
                      className={`form-input ${errors.name ? "input-error" : ""}`}
                    />
                  </div>
                  {errors.name && <small className="text-danger ms-1">{errors.name}</small>}
                </Form.Group>
              )}

              {/* Email */}
              <Form.Group className="form-group">
                <div className="input-wrapper">
                  <Mail className="input-icon" size={20} />
                  <Form.Control
                    type="email"
                    placeholder="Email Address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={`form-input ${errors.email ? "input-error" : ""}`}
                  />
                </div>
                {errors.email && <small className="text-danger ms-1">{errors.email}</small>}
              </Form.Group>

              {/* Phone - signup only */}
              {!isLoginMode && (
                <Form.Group className="form-group">
                  <div className="input-wrapper">
                    <Phone className="input-icon" size={20} />
                    <Form.Control
                      type="tel"
                      placeholder="Phone Number"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className={`form-input ${errors.phone ? "input-error" : ""}`}
                    />
                  </div>
                  {errors.phone && <small className="text-danger ms-1">{errors.phone}</small>}
                </Form.Group>
              )}

              {/* Password */}
              <Form.Group className="form-group">
                <div className="input-wrapper">
                  <Lock className="input-icon" size={20} />
                  <Form.Control
                    type={showPassword ? "text" : "password"}
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={`form-input ${errors.password ? "input-error" : ""}`}
                  />
                  <div className="eye-icon" onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </div>
                </div>
                {errors.password && <small className="text-danger ms-1">{errors.password}</small>}
              </Form.Group>

              {/* Submit Button */}
              <Button type="submit" className="login-btn" disabled={isSubmitting}>
                {isSubmitting ? "Processing..." : isLoginMode ? "Login" : "Sign Up"}
              </Button>

              {/* Social Login */}
              <div className="social-login-section">
                <div className="divider"><span>OR</span></div>
                <div className="social-buttons">
                  <button type="button" className="social-btn google-btn" onClick={() => handleSocialLogin("Google")}>
                    Login with Google
                  </button>
                  <button type="button" className="social-btn facebook-btn" onClick={() => handleSocialLogin("Facebook")}>
                    Login with Facebook
                  </button>
                </div>
              </div>

              {/* Footer */}
              <div className="form-footer">
                <span className="footer-link">
                  {isLoginMode ? "Don't have account?" : "Already have an account?"}
                </span>
                {/* <span className="signup-link" onClick={toggleAuthMode}>
                  {isLoginMode ? "Sign up" : "Login"}
                </span> */}
              </div>

            </Form>
          </div>

          {/* Right Side - Welcome */}
          <div className="first-welcome-section">
            <div className="welcome-content">
              <h1 className="welcome-title">
                {isLoginMode ? (<>WELCOME<br />BACK!</>) : (<>JOIN<br />US!</>)}
              </h1>
              <p className="first-welcome-text">
                {isLoginMode ? "We're happy to have you back again!" : "Start your journey with us today!"}
              </p>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default Ngologin;